/**
 * 관심종목 등록/제거 추적 훅
 * 
 * 관심종목을 등록하거나 제거하는 기능
 * - start()는 세션 스토리지를 확인하여 저장된 의도가 있으면 자동 복원
 * - 로그인 후 리다이렉트되어도 같은 의도 유지
 * - 로그인 의도는 자동으로 부모 의도 연결 (parentIntentType)
 * 
 * @example
 * ```tsx
 * // 단일 종목 등록
 * function StockDetail({ ticker, market }) {
 *   const watchlist = useWatchlist('add');
 *   const loginIntent = useAuth();
 *   const isLoggedIn = useAuth().isLoggedIn;
 *   
 *   const handleAdd = async () => {
 *     watchlist.start({
 *       ticker,
 *       market,
 *       cat: 'stock',
 *       from_list_id: 'stock_detail_header'
 *     });
 *     
 *     if (!isLoggedIn) {
 *       loginIntent.start(
 *         { redirectUrl: router.asPath },
 *         { parentIntentType: 'watchlist_add' }
 *       );
 *       router.push('/login');
 *       return;
 *     }
 *     
 *     try {
 *       await apiAddToWatchlist(ticker, market);
 *       watchlist.success();
 *     } catch (error) {
 *       watchlist.fail('backend_5xx');
 *     }
 *   };
 *   
 *   return <button onClick={handleAdd}>관심 추가</button>;
 * }
 * 
 * // 여러 종목 한번에 등록
 * function BulkAdd({ tickers }) {
 *   const watchlist = useWatchlist('add');
 *   
 *   const handleBulkAdd = async () => {
 *     watchlist.start({
 *       ticker: tickers,  // 👈 배열도 가능
 *       market: 'kr',
 *       from_list_id: 'strategy_detail_stocks'
 *     });
 *     
 *     try {
 *       await apiBulkAddToWatchlist(tickers);
 *       watchlist.success();
 *     } catch (error) {
 *       watchlist.fail('backend_5xx');
 *     }
 *   };
 * }
 * 
 * // 종목 제거
 * function StockDetail({ ticker, market }) {
 *   const watchlist = useWatchlist('remove');
 *   
 *   const handleRemove = async () => {
 *     watchlist.start({
 *       ticker,
 *       market,
 *       from_list_id: 'watchlist_page'
 *     });
 *     
 *     try {
 *       await apiRemoveFromWatchlist(ticker);
 *       watchlist.success();
 *     } catch (error) {
 *       watchlist.fail('backend_error');
 *     }
 *   };
 *   
 *   return <button onClick={handleRemove}>관심 해제</button>;
 * }
 * ```
 */

import { useCallback } from 'react';
import { track } from '../../../core/client';
import { useIntent } from './useIntent';

/**
 * 관심종목 액션 타입
 */
export type WatchlistAction = 'add' | 'remove';

/**
 * 관심종목 컨텍스트 (등록/제거 공통)
 */
export interface WatchlistContext {
    ticker: string | string[];  // 단일 또는 배열
    market?: 'kr' | 'us' | ('kr' | 'us')[];  // 단일, 배열, 또는 생략 (ticker와 1:1 매칭)
    cat?: 'stock' | 'index' | 'crypto';
    from_list_id: string;
    rank_pos?: number;
}

export interface WatchlistActions {
    start: (context: WatchlistContext) => void;
    success: () => void;
    fail: (reason?: string) => void;
}

export interface WatchlistBothActions {
    add: WatchlistActions;
    remove: WatchlistActions;
}

/**
 * 관심종목 등록/제거 통합 훅
 * 
 * @param action - 'add' | 'remove' | undefined
 * @returns action이 있으면 WatchlistActions, 없으면 { add, remove }
 * 
 * @example
 * // 방법 1: 하나만 사용
 * const watchlist = useWatchlist('add');
 * watchlist.start({ ... });
 * 
 * // 방법 2: 둘 다 사용
 * const { add, remove } = useWatchlist();
 * add.start({ ... });
 * remove.start({ ... });
 */
export function useWatchlist(): WatchlistBothActions;
export function useWatchlist(action: WatchlistAction): WatchlistActions;
export function useWatchlist(action?: WatchlistAction): WatchlistActions | WatchlistBothActions {
    // 항상 두 개의 intent를 생성 (Hook 규칙 준수)
    const addIntent = useIntent<WatchlistContext>({
        type: 'watchlist_add',
        requiredGate: 'login',
        onStart: ({ intent_id, context, parent_intent_id, required_gate }) => {
            if (!context) {
                console.warn('⚠️ [WatchlistAdd] start() requires context');
                return;
            }
            track('watchlist_add_start', {
                intent_id,
                ticker: context.ticker,
                market: context.market,
                cat: context.cat,
                from_list_id: context.from_list_id,
                rank_pos: context.rank_pos,
                parent_intent_id,
                required_gate,
            });
        },
        onSuccess: ({ intent_id, context, latency_ms }) => {
            if (!context) return;
            track('watchlist_add_success', {
                intent_id,
                ticker: context.ticker,
                market: context.market,
                cat: context.cat,
                from_list_id: context.from_list_id,
                rank_pos: context.rank_pos,
                latency_ms,
            });
        },
        onFail: ({ intent_id, context, fail_reason }) => {
            if (!context) return;
            track('watchlist_add_fail', {
                intent_id,
                ticker: context.ticker,
                market: context.market,
                cat: context.cat,
                from_list_id: context.from_list_id,
                rank_pos: context.rank_pos,
                fail_reason,
            });
        },
    });

    const removeIntent = useIntent<WatchlistContext>({
        type: 'watchlist_remove',
        requiredGate: 'login',
        onStart: ({ intent_id, context, parent_intent_id, required_gate }) => {
            if (!context) {
                console.warn('⚠️ [WatchlistRemove] start() requires context');
                return;
            }
            track('watchlist_remove_start', {
                intent_id,
                ticker: context.ticker,
                market: context.market,
                cat: context.cat,
                from_list_id: context.from_list_id,
                rank_pos: context.rank_pos,
                parent_intent_id,
                required_gate,
            });
        },
        onSuccess: ({ intent_id, context, latency_ms }) => {
            if (!context) return;
            track('watchlist_remove_success', {
                intent_id,
                ticker: context.ticker,
                market: context.market,
                cat: context.cat,
                from_list_id: context.from_list_id,
                rank_pos: context.rank_pos,
                latency_ms,
            });
        },
        onFail: ({ intent_id, context, fail_reason }) => {
            if (!context) return;
            track('watchlist_remove_fail', {
                intent_id,
                ticker: context.ticker,
                market: context.market,
                cat: context.cat,
                from_list_id: context.from_list_id,
                rank_pos: context.rank_pos,
                fail_reason,
            });
        },
    });

    const addActions: WatchlistActions = {
        start: useCallback((context: WatchlistContext) => {
            addIntent.start(context);
        }, [addIntent]),
        success: useCallback(() => {
            addIntent.success();
        }, [addIntent]),
        fail: useCallback((reason?: string) => {
            addIntent.fail(reason);
        }, [addIntent]),
    };

    const removeActions: WatchlistActions = {
        start: useCallback((context: WatchlistContext) => {
            removeIntent.start(context);
        }, [removeIntent]),
        success: useCallback(() => {
            removeIntent.success();
        }, [removeIntent]),
        fail: useCallback((reason?: string) => {
            removeIntent.fail(reason);
        }, [removeIntent]),
    };

    // action에 따라 반환
    if (!action) {
        return { add: addActions, remove: removeActions };
    }

    return action === 'add' ? addActions : removeActions;
}

/**
 * 관심종목 등록 훅 (기존 호환성)
 * @deprecated useWatchlist('add') 사용 권장
 */
export function useWatchlistAdd(): WatchlistActions {
    return useWatchlist('add');
}

/**
 * 관심종목 제거 훅 (기존 호환성)
 * @deprecated useWatchlist('remove') 사용 권장
 */
export function useWatchlistRemove(): WatchlistActions {
    return useWatchlist('remove');
}
